import React from "react";
import { HrMetricIcon } from "./HrMetricIcon";
import { intensityPercent } from "../features/trainingSummary/zoneUtils";

/**
 * Compact HR metric tiles for exercise / session cards.
 * - metrics="hr": Max / Avg / Min HR (home session cards)
 * - metrics="intensity": Max HR / Max Intensity / Avg Intensity (session results)
 * - metrics="session_highlights": best player records per metric (home session cards)
 */
export function ExerciseCardHrSummary({
  hrStats,
  hrHighlights = null,
  t,
  className = "",
  mhr = null,
  metrics = "hr",
}) {
  const maxHr =
    hrStats?.maxHR != null && hrStats.maxHR !== ""
      ? Math.round(Number(hrStats.maxHR))
      : null;
  const avgHr =
    hrStats?.averageHR != null && hrStats.averageHR !== ""
      ? Math.round(Number(hrStats.averageHR))
      : null;
  const minHr =
    hrStats?.minHR != null && hrStats.minHR !== ""
      ? Math.round(Number(hrStats.minHR))
      : null;

  const maxIntensity =
    metrics === "intensity" && mhr > 0 && maxHr > 0
      ? intensityPercent(maxHr, mhr)
      : null;
  const avgIntensity =
    metrics === "intensity" && mhr > 0 && avgHr > 0
      ? intensityPercent(avgHr, mhr)
      : null;

  const cardClass = [
    "player-exercise-details__hr-summary-card",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const tiles =
    metrics === "session_highlights"
      ? [
          {
            variant: "max",
            label: t("max_hr_short", "Max HR"),
            value:
              hrHighlights?.max_hr?.value > 0
                ? Math.round(hrHighlights.max_hr.value)
                : "—",
            unit: hrHighlights?.max_hr?.value > 0 ? "bpm" : "",
            playerName: hrHighlights?.max_hr?.player_name || null,
          },
          {
            variant: "max_intensity",
            label: t("max_intensity_short", "Max Int."),
            value:
              hrHighlights?.max_intensity?.value > 0
                ? Math.round(hrHighlights.max_intensity.value)
                : "—",
            unit: hrHighlights?.max_intensity?.value > 0 ? "%" : "",
            playerName: hrHighlights?.max_intensity?.player_name || null,
          },
          {
            variant: "avg_intensity",
            label: t("avg_intensity_short", "Avg Int."),
            value:
              hrHighlights?.avg_intensity?.value > 0
                ? Math.round(hrHighlights.avg_intensity.value)
                : "—",
            unit: hrHighlights?.avg_intensity?.value > 0 ? "%" : "",
            playerName: hrHighlights?.avg_intensity?.player_name || null,
          },
        ]
      : metrics === "intensity"
        ? [
            {
              variant: "max",
              label: t("max_hr", "Max HR"),
              value: maxHr > 0 ? maxHr : "—",
              unit: maxHr > 0 ? "bpm" : "",
            },
            {
              variant: "max_intensity",
              label: t("max_intensity", "Max Intensity"),
              value: maxIntensity > 0 ? maxIntensity : "—",
              unit: maxIntensity > 0 ? "%" : "",
            },
            {
              variant: "avg_intensity",
              label: t("avg_intensity", "Avg Intensity"),
              value: avgIntensity > 0 ? avgIntensity : "—",
              unit: avgIntensity > 0 ? "%" : "",
            },
          ]
        : [
            {
              variant: "max",
              label: t("max_hr", "Max HR"),
              value: maxHr > 0 ? maxHr : "—",
              unit: maxHr > 0 ? "bpm" : "",
            },
            {
              variant: "avg",
              label: t("avg_hr", "Avg HR"),
              value: avgHr > 0 ? avgHr : "—",
              unit: avgHr > 0 ? "bpm" : "",
            },
            {
              variant: "min",
              label: t("min_hr", "Min HR"),
              value: minHr > 0 ? minHr : "—",
              unit: minHr > 0 ? "bpm" : "",
            },
          ];

  const hasHighlightData =
    metrics === "session_highlights" &&
    tiles.some((tile) => tile.value !== "—");

  if (metrics === "session_highlights" && !hasHighlightData) {
    return null;
  }

  return (
    <div className={cardClass}>
      {tiles.map((tile) => (
        <div
          key={tile.variant}
          className={`player-exercise-details__hr-subcard player-exercise-details__hr-subcard--${tile.variant}`}
        >
          <div className="player-exercise-details__hr-subcard-header">
            <HrMetricIcon
              variant={tile.variant}
              size={14}
              className={`player-exercise-details__hr-subcard-icon player-exercise-details__hr-subcard-icon--${tile.variant}`}
            />
            <span>{tile.label}</span>
          </div>
          <div className="player-exercise-details__hr-subcard-value">
            {tile.value}
            {tile.unit ? (
              <>
                {" "}
                <span className="player-exercise-details__hr-subcard-unit">
                  {tile.unit}
                </span>
              </>
            ) : null}
          </div>
          {tile.playerName ? (
            <span className="home-session-card__hr-player-name">
              {tile.playerName}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default ExerciseCardHrSummary;
